// src/features/properties/PropertiesList.js
import { selectAllProperties, selectPropertiesPagination } from './PropertiesApiSlice'
import { useGetPropertiesQuery } from './PropertiesApiSlice'
import { useSelector } from 'react-redux'
import Property from './Property'
import { useState, useEffect } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import imagesLoaded from 'imagesloaded';

const PropertiesList = ({ page, setPage, quickSearch = '', phoneSearch = '', district = '', propertyType = '', newWard = '', position = '', numbers = [], dateType = 'updatedAt' }) => {

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error, 
        refetch
    } = useGetPropertiesQuery({ page, limit: 20, quickSearch, phoneSearch, district, propertyType, newWard, position, numbers, dateType })

    const properties = data?.ids?.map(id => data.entities[id]) ?? []
    const pagination = data?.pagination ?? { current: 1, pages: 1, total: 0 }
    const hasProperties = properties.length > 0
    
    useEffect(() => {

        if (isFetching) return;
        if (properties.length === 0 && pagination.current > 1) {
            const newPage = Math.max(1, pagination.current - 1);
            setPage(newPage)
        }

        const grid = document.querySelector('[data-masonry]');
        if (grid && window.Masonry) {
            let msnry = grid._masonry; 
            if (!msnry) {
                msnry = new window.Masonry(grid, {
                    percentPosition: true,
                    itemSelector: '.col-6.col-lg-3',
                    columnWidth: '.col-6.col-lg-3',
                });
                grid._masonry = msnry; 
            }
            msnry.reloadItems();
            msnry.layout();
            const imgLoad = imagesLoaded(grid);
            imgLoad.on('always', () => {
                msnry.layout();
            });
        }
    }, [properties.length, pagination.current, isFetching])

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-light">Đang tải bất động sản...</p>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="text-center py-5 text-danger">
                Lỗi tải dữ liệu
                <Button variant="outline-danger" size="sm" className="ms-3" onClick={refetch}>
                    Thử lại
                </Button>
            </div>
        )
    }

    const generatePageNumbers = () => {
        const totalPages = pagination.pages || 1
        const current = pagination.current || 1
        const pages = []

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            if (current <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages)
            } else if (current >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
            } else {
                pages.push(1, '...', current - 1, current, current + 1, '...', totalPages)
            }
        }
        return pages
    }

    const scrollToTop = () => {
        setTimeout(() => {
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 100);
    };

    // Lỗi
    if (isError) {
        return <div className="text-center py-5 text-danger">Lỗi tải dữ liệu</div>
    }

    return (
        <>
            {/* Nút Tải lại + thông báo khi đang refetch */}
            <div className="text-center mb-4">
                <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => refetch()}
                    disabled={isFetching}
                >
                    {isFetching ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Đang cập nhật...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            Tải lại dữ liệu mới nhất
                        </>
                    )}
                </Button>

                {/* Thông báo nhỏ khi đang refetch */}
                {isFetching && !isLoading && (
                    <div className="text-info mt-2 small">
                        <i className="bi bi-cloud-download me-1"></i>
                        Đang tải dữ liệu mới...
                    </div>
                )}
            </div>

            {/* === DANH SÁCH BĐS === */}
            <div className={`row g-3 min-vh-50 ${isFetching ? 'opacity-75' : ''}`} 
                data-masonry='{"percentPosition": true}'>
                {hasProperties ? (
                    properties.map(prop => (
                        <div className="col-6 col-lg-3" key={prop._id}>
                            <Property property={prop} />
                        </div>
                    ))
                ) : (
                    <div className="col-12">
                        <div className="text-center py-5 my-5">
                            <i className="bi bi-house-slash display-1 text-light opacity-50"></i>
                            <h4 className="mt-4 text-light fw-normal">
                                Không tìm thấy bất động sản nào
                            </h4>
                            <p className="text-light">
                                Hãy thử thay đổi từ khóa hoặc bộ lọc
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* PHÂN TRANG NÂNG CẤP */}
            {pagination.pages > 1 && (
                <div className="py-4 mt-5">
                    <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={pagination.current === 1}
                            onClick={() => {
                                setPage(1)
                                scrollToTop()
                            }}
                        >
                            ‹‹ Đầu
                        </Button>

                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!pagination.hasPrev}
                            onClick={() => {
                                setPage(p => p - 1)
                                scrollToTop()
                            }}
                        >
                            ‹ Trước
                        </Button>

                        {/* Số trang */}
                        {generatePageNumbers().map((p, idx) => (
                            p === '...' ? (
                                <span key={`dots-${idx}`} className="px-3 text-light">...</span>
                            ) : (
                                <Button
                                    key={p}
                                    variant={pagination.current === p ? "primary" : "outline-primary"}
                                    size="sm"
                                    onClick={() => {
                                        setPage(p)
                                        scrollToTop()
                                    }}
                                    className="px-3"
                                >
                                    {p}
                                </Button>
                            )
                        ))}

                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!pagination.hasNext}
                            onClick={() => {
                                setPage(p => p + 1)
                                scrollToTop()
                            }}
                        >
                            Sau ›
                        </Button>

                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={pagination.current === pagination.pages}
                            onClick={() => {
                                setPage(pagination.pages)
                                scrollToTop()
                            }}
                        >
                            Cuối ››
                        </Button>
                    </div>
                    <div className="text-center mt-3">
                        <form onSubmit={(e) => {
                            e.preventDefault()
                            const input = e.target.elements.goPage.value.trim()
                            const target = parseInt(input)
                            if (target >= 1 && target <= pagination.pages) {
                                setPage(target)
                                scrollToTop()
                            }
                            e.target.elements.goPage.value = ''
                        }} className="d-inline-block">
                            <small className="text-light me-2">Đi đến trang:</small>
                            <input
                                type="number"
                                name="goPage"
                                min="1"
                                max={pagination.pages}
                                placeholder={pagination.current}
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: '80px' }}
                            />
                            <Button type="submit" variant="outline-primary" size="sm" className="ms-2">
                                Đi
                            </Button>
                        </form>
                    </div>
                    {/* Thông tin tổng */}
                    <div className="text-center mt-3 text-light">
                        <small>
                            Trang <strong>{pagination.current}</strong> / <strong>{pagination.pages}</strong> 
                            {' '} ({pagination.total} bất động sản)
                        </small>
                    </div>

                </div>
            )}
        </>
    )
}
export default PropertiesList